import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/store/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

const CONDITIONS = ["NEW", "OPEN BOX", "LIKE NEW", "USED", "RETURNED", "REFURBISHED", "CLEARANCE"];

export default function Shop({ presetBadge, title, subtitle }) {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, per_page: 24 });
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const condition = params.get("condition") || "";
  const brand = params.get("brand") || "";
  const min_price = params.get("min_price") || "";
  const max_price = params.get("max_price") || "";
  const badge = presetBadge || params.get("badge") || "";
  const sort = params.get("sort") || "newest";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    document.title = title
      ? `${title} · Barrhaven Dealswagon Outlet`
      : "Shop All · Barrhaven Dealswagon Outlet";
  }, [title]);

  useEffect(() => {
    setLoading(true);
    const query = { sort, page, per_page: 24 };
    if (q) query.q = q;
    if (category) query.category = category;
    if (condition) query.condition = condition;
    if (brand) query.brand = brand;
    if (min_price) query.min_price = min_price;
    if (max_price) query.max_price = max_price;
    query.availability = "in_stock"; // never show sold-out items on the storefront
    if (badge) query.badge = badge;
    api.get("/products", { params: query }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q, category, condition, brand, min_price, max_price, badge, sort, page]);

  const brands = useMemo(() => {
    const set = new Set(data.items.map((p) => p.brand).filter(Boolean));
    return Array.from(set);
  }, [data.items]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v == null || v === "") next.delete(k);
    else next.set(k, v);
    if (k !== "page") next.delete("page"); // reset to page 1 only when a *filter* changes
    setParams(next);
  };
  const clearFilters = () => {
    const keep = new URLSearchParams();
    if (q) keep.set("q", q);
    setParams(keep);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.per_page));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="shop-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl text-brand-navy">
          {title || (q ? `Results for "${q}"` : "Shop All")}
        </h1>
        {subtitle && <p className="text-slate-600 mt-1">{subtitle}</p>}
        <div className="text-sm text-slate-500 mt-1" data-testid="shop-result-count">
          {loading ? "Loading..." : `${data.total} item${data.total === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4">
        <button data-testid="shop-filter-toggle" onClick={() => setFilterOpen((v) => !v)}
          className="lg:hidden inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 h-10 text-sm font-bold">
          <SlidersHorizontal className="h-4 w-4" /> Filters
        </button>
        <select data-testid="shop-sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)}
          className="ml-auto border border-slate-200 rounded-full px-4 h-10 text-sm font-semibold bg-white">
          <option value="newest">Newest</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="popular">Popular</option>
        </select>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className={`${filterOpen ? "block" : "hidden"} lg:block bg-white rounded-xl border border-slate-200 p-4 h-fit sticky top-24`}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-lg text-brand-navy">Filters</div>
            <button onClick={clearFilters} data-testid="shop-clear-filters" className="text-xs text-brand-red font-bold">Clear</button>
          </div>

          <FilterGroup title="Category">
            <div className="max-h-56 overflow-y-auto space-y-1">
              <button onClick={() => setParam("category", "")}
                className={`w-full text-left text-sm px-2 py-1.5 rounded ${!category ? "bg-brand-navy text-white" : "hover:bg-slate-100"}`}>
                All Categories
              </button>
              {cats.map((c) => (
                <button key={c.id} data-testid={`filter-category-${c.slug}`} onClick={() => setParam("category", c.slug)}
                  className={`w-full text-left text-sm px-2 py-1.5 rounded ${category === c.slug ? "bg-brand-navy text-white" : "hover:bg-slate-100"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup title="Condition">
            <div className="flex flex-wrap gap-1.5">
              {CONDITIONS.map((c) => (
                <button key={c} data-testid={`filter-condition-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setParam("condition", condition === c ? "" : c)}
                  className={`text-[11px] font-black uppercase tracking-wider px-2 py-1 rounded ${condition === c ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                  {c}
                </button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup title="Price">
            <div className="flex gap-2">
              <input data-testid="filter-min-price" type="number" placeholder="Min" value={min_price}
                onChange={(e) => setParam("min_price", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded px-2 text-sm" />
              <input data-testid="filter-max-price" type="number" placeholder="Max" value={max_price}
                onChange={(e) => setParam("max_price", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded px-2 text-sm" />
            </div>
          </FilterGroup>

          {brands.length > 0 && (
            <FilterGroup title="Brand">
              <select data-testid="filter-brand" value={brand} onChange={(e) => setParam("brand", e.target.value)}
                className="w-full h-9 border border-slate-200 rounded px-2 text-sm bg-white">
                <option value="">All brands</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </FilterGroup>
          )}

          <button onClick={() => setFilterOpen(false)} className="lg:hidden mt-2 w-full bg-brand-navy text-white rounded-full py-2 font-bold">
            <X className="inline h-4 w-4 mr-1" /> Close
          </button>
        </aside>

        <section>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-slate-300 rounded-xl bg-white">
              <div className="font-heading text-2xl text-brand-navy">No products found</div>
              <p className="text-slate-500 mt-2">Try clearing filters or searching a different term.</p>
              <button onClick={clearFilters} data-testid="shop-empty-clear" className="mt-4 inline-flex bg-brand-navy text-white rounded-full px-5 h-10 font-bold">
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button key={i} data-testid={`shop-page-${i + 1}`} onClick={() => setParam("page", i + 1)}
                      className={`h-9 min-w-[36px] px-3 rounded-full text-sm font-bold ${page === i + 1 ? "bg-brand-navy text-white" : "bg-white border border-slate-200 hover:bg-slate-50"}`}>
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}

const FilterGroup = ({ title, children }) => (
  <div className="py-3 border-t border-slate-200 first:border-t-0">
    <div className="font-bold text-sm text-brand-navy mb-2">{title}</div>
    {children}
  </div>
);
